import React, { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';

const ClientPortal = ({ onBackToHome, onLogout, isLoggedIn, onFindTrainer }) => {
  const [activeTab, setActiveTab] = useState('find-trainer');
  const [pets, setPets] = useState([]);
  const [profile, setProfile] = useState(null);
  const [activeTrainers, setActiveTrainers] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [trainerFilters, setTrainerFilters] = useState({
    search: '',
    philosophy: [],
    expertise: [],
    credentials: []
  });
  const [openDropdowns, setOpenDropdowns] = useState({
    philosophy: false,
    expertise: false,
    credentials: false
  });
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionDeliverables, setSessionDeliverables] = useState({}); // Map session_request_id to deliverables
  const [activeTrainingRequests, setActiveTrainingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedPetId, setSelectedPetId] = useState('');
  const [requestExplanation, setRequestExplanation] = useState('');
  const [requestDuration, setRequestDuration] = useState(30);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileEditData, setProfileEditData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    address: '',
    password: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showAddPetModal, setShowAddPetModal] = useState(false);
  const [newPetData, setNewPetData] = useState({
    pet_name: '',
    age: '',
    race: '',
    target_behaviors: ''
  });
  const [isEditingPet, setIsEditingPet] = useState(null);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedRequestForPayment, setSelectedRequestForPayment] = useState(null);
  const [couponCode, setCouponCode] = useState('');

  const clientId = localStorage.getItem('clientId');

  const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '$0.00';
    }
    return `$${Number(value).toFixed(2)}`;
  };

  const normalizeTasks = (rawTasks) => {
    if (!rawTasks) return [];

    let parsed = rawTasks;
    if (typeof rawTasks === 'string') {
      try {
        parsed = JSON.parse(rawTasks);
      } catch (err) {
        parsed = [rawTasks];
      }
    }

    if (!Array.isArray(parsed)) {
      parsed = [parsed];
    }

    return parsed
      .map((task) => {
        if (typeof task === 'string') {
          return {
            description: task,
            completed: false
          };
        }
        if (task && typeof task === 'object') {
          const description = task.description || task.title || '';
          if (!description) return null;
          return {
            description,
            completed: Boolean(task.completed),
            notes: task.notes || ''
          };
        }
        return null;
      })
      .filter(Boolean);
  };
  console.log('=== CLIENT PORTAL LOADED ===');
  console.log('Client ID from localStorage:', clientId);
  console.log('All localStorage:', localStorage);
  console.log('isLoggedIn prop:', isLoggedIn);

  const applyTrainerFilters = () => {
    let filtered = [...activeTrainers];

    if (trainerFilters.search) {
      filtered = filtered.filter(trainer => 
        trainer.name.toLowerCase().includes(trainerFilters.search.toLowerCase()) ||
        trainer.bio.toLowerCase().includes(trainerFilters.search.toLowerCase())
      );
    }

    if (trainerFilters.philosophy && trainerFilters.philosophy.length > 0) {
      filtered = filtered.filter(trainer => 
        trainer.philosophy && trainerFilters.philosophy.some(phil => trainer.philosophy.includes(phil))
      );
    }

    if (trainerFilters.expertise && trainerFilters.expertise.length > 0) {
      filtered = filtered.filter(trainer => 
        trainer.specializations && trainerFilters.expertise.some(exp => trainer.specializations.includes(exp))
      );
    }

    if (trainerFilters.credentials && trainerFilters.credentials.length > 0) {
      filtered = filtered.filter(trainer => 
        trainer.credentials && trainerFilters.credentials.some(cred => trainer.credentials.includes(cred))
      );
    }

    setFilteredTrainers(filtered);
  };

  const handleFilterToggle = (filterType, value) => {
    setTrainerFilters(prev => {
      const currentArray = prev[filterType] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return {
        ...prev,
        [filterType]: newArray
      };
    });
  };

  const removeFilter = (filterType, value) => {
    setTrainerFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].filter(item => item !== value)
    }));
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownName]: !prev[dropdownName]
    }));
  };

  useEffect(() => {
    applyTrainerFilters();
  }, [activeTrainers, trainerFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown]')) {
        setOpenDropdowns({
          philosophy: false,
          expertise: false,
          credentials: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (clientId) {
      loadInitialData();
    }
  }, [clientId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load trainers by default since activeTab starts as 'find-trainer'
      if (activeTab === 'find-trainer') {
        await loadActiveTrainers();
      } else if (activeTab === 'pets') {
        await loadPets();
      }
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadPets = async () => {
    try {
      console.log('=== LOADING PETS ===');
      console.log('Client ID:', clientId);
      const response = await apiService.getClientPets(clientId);
      console.log('Pets API response:', response);
      // Backend returns "Data" (capital D), not "data"
      const petsData = response.Data || [];
      console.log('Pets data extracted:', petsData);
      setPets(petsData);
      console.log('Pets state set to:', petsData);
    } catch (err) {
      console.error('Error loading pets:', err);
      setError('Failed to load pets');
    }
  };

  const loadProfile = async () => {
    try {
      const response = await apiService.getClientProfile(clientId);
      if (response.Code === 200) {
        setProfile(response.data);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
    }
  };

  const handleClientPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    if (!clientId) {
      setError('Client ID not found');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const response = await apiService.uploadClientProfilePhoto(clientId, file);
      
      if (response.Code === 200) {
        // Reload profile to get updated photo URL
        await loadProfile();
        alert('Profile photo uploaded successfully!');
      } else {
        setError(response.Message || 'Failed to upload photo');
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      setError(err.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const loadActiveTrainers = async () => {
    try {
      setRefreshSuccess(false);
      setLoading(true);
      console.log('=== LOADING ACTIVE TRAINERS ===');
      console.log('Current time:', new Date().toISOString());
      console.log('Loading active trainers...');
      
      // Force a fresh API call by adding a timestamp parameter
      const response = await apiService.getActiveTrainers();
      console.log('Active trainers response:', response);
      console.log('Response Code:', response.Code);
      console.log('Response trainers:', response.trainers);
      console.log('Response trainers length:', response.trainers ? response.trainers.length : 'no trainers');
      
      if (response.Code === 200 && response.trainers && response.trainers.length > 0) {
        console.log('Transforming trainer data...');
        
        // Helper function to parse Python-style list strings
        const parseListString = (value) => {
          if (!value) return [];
          if (Array.isArray(value)) return value;
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return [];
            
            // Try JSON.parse first (handles JSON arrays with double quotes)
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) return parsed;
            } catch (err) {
              // Not valid JSON, continue
            }
            
            // Try to parse Python-style list strings (e.g., "['item1', 'item2']")
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
              try {
                // Replace single quotes with double quotes for JSON parsing
                const jsonString = trimmed.replace(/'/g, '"');
                const parsed = JSON.parse(jsonString);
                if (Array.isArray(parsed)) return parsed;
              } catch (err) {
                // If that fails, try to extract items manually
                const inner = trimmed.slice(1, -1).trim();
                if (inner) {
                  const items = inner.split(',').map(item => {
                    let cleaned = item.trim();
                    // Remove surrounding quotes if present
                    if ((cleaned.startsWith("'") && cleaned.endsWith("'")) || 
                        (cleaned.startsWith('"') && cleaned.endsWith('"'))) {
                      cleaned = cleaned.slice(1, -1);
                    }
                    return cleaned.trim();
                  }).filter(Boolean);
                  if (items.length > 0) return items;
                }
              }
            }
            
            // Fall back to comma-separated
            return trimmed.split(',').map(item => {
              let cleaned = item.trim();
              if ((cleaned.startsWith("'") && cleaned.endsWith("'")) || 
                  (cleaned.startsWith('"') && cleaned.endsWith('"'))) {
                cleaned = cleaned.slice(1, -1);
              }
              return cleaned.trim();
            }).filter(Boolean);
          }
          return [];
        };
        
        // Helper to parse price
        const parsePrice = (value) => {
          if (value === null || value === undefined || value === '') return 0;
          const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
          return isNaN(parsed) ? 0 : parsed;
        };
        
        // Transform backend data to match frontend format
        const transformedTrainers = response.trainers.map(trainer => {
          const first10MinRate = parsePrice(trainer.first_10_min_rate);
          const perMinuteRate = parsePrice(trainer.per_minute_rate);
          const expertise = parseListString(trainer.areas_of_expertise);
          const philosophy = parseListString(trainer.training_philosophy);
          const credentials = parseListString(trainer.credentials);
          
          return {
            id: trainer.id,
            name: `${trainer.first_name} ${trainer.last_name}`,
            title: `Professional Dog Trainer`,
            experience: `${trainer.experience_years || 0}+ years`,
            photo: trainer.first_name.charAt(0) + trainer.last_name.charAt(0),
            specializations: expertise,
            philosophy: philosophy,
            credentials: credentials,
            baseRate: first10MinRate,
            perMinuteRate: perMinuteRate.toFixed(2),
            overallRating: trainer.average_rating || 0,
            totalReviews: trainer.total_reviews || 0,
            isOnline: trainer.isOnline || false,
            bio: trainer.bio || 'Professional dog trainer',
            reviews: [] // We'll load reviews separately if needed
          };
        });
        console.log('Transformed trainers:', transformedTrainers);
        setActiveTrainers(transformedTrainers);
        console.log('Set activeTrainers state to:', transformedTrainers);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      } else {
        console.log('No trainers found or invalid response');
        setActiveTrainers([]);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Error loading trainers:', err);
      setError('Failed to load active trainers');
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTrainingRequests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getActiveTrainingRequests(clientId);
      console.log('Active training requests response:', response);
      
      if (response.Code === 200 && response.data) {
        const transformedRequests = response.data.map(request => ({
          ...request,
          first_10_min_rate: parseFloat(request.first_10_min_rate || request.base_rate || 0),
          per_minute_rate: parseFloat(request.per_minute_rate || 0),
          total_price: request.total_price !== undefined && request.total_price !== null ? parseFloat(request.total_price) : null,
          duration_minutes: request.duration_minutes || null,
          created_at: request.created_at || null,
          accepted_at: request.accepted_at || null,
          payment_status: request.payment_status || 'pending',
          client_joined_at: request.client_joined_at || null,
          trainer_joined_at: request.trainer_joined_at || null
        }));
        setActiveTrainingRequests(transformedRequests);
      } else {
        setActiveTrainingRequests([]);
      }
    } catch (err) {
      console.error('Error loading training requests:', err);
      setError('Failed to load training requests');
    } finally {
      setLoading(false);
    }
  };

  const loadSessionHistory = async () => {
    try {
      setLoading(true);
      const response = await apiService.getClientSessions(clientId);
      if (response.Code === 200) {
        const sessions = response.data || [];
        
        // Load ratings for all sessions
        const ratingsPromises = sessions
          .filter(session => session.session_request_id)
          .map(async (session) => {
            try {
              const ratingResponse = await apiService.getSessionRating(session.session_request_id);
              if (ratingResponse.Code === 200 && ratingResponse.data) {
                return { session_request_id: session.session_request_id, rating: ratingResponse.data.rating };
              }
            } catch (err) {
              console.log(`No rating found for session request ${session.session_request_id}`);
            }
            return null;
          });
        
        const ratingsResults = await Promise.all(ratingsPromises);
        const ratingsMap = {};
        ratingsResults.forEach(result => {
          if (result) {
            ratingsMap[result.session_request_id] = result.rating;
          }
        });
        
        // Add user_rating to each session
        const sessionsWithRatings = sessions.map(session => ({
          ...session,
          user_rating: session.session_request_id ? ratingsMap[session.session_request_id] : null
        }));
        
        setSessionHistory(sessionsWithRatings);
        
        // Load deliverables for all sessions that have session_request_id
        const deliverablesPromises = sessions
          .filter(session => session.session_request_id)
          .map(async (session) => {
            try {
              const deliverablesResponse = await apiService.getDeliverables(session.session_request_id);
              if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                return { session_request_id: session.session_request_id, data: deliverablesResponse.data };
              }
            } catch (err) {
              console.log(`No deliverables found for session request ${session.session_request_id}`);
            }
            return null;
          });
        
        const deliverablesResults = await Promise.all(deliverablesPromises);
        const deliverablesMap = {};
        deliverablesResults.forEach(result => {
          if (result) {
            deliverablesMap[result.session_request_id] = result.data;
          }
        });
        setSessionDeliverables(deliverablesMap);
      } else {
        setSessionHistory([]);
      }
    } catch (err) {
      console.error('Error loading session history:', err);
      setError('Failed to load session history');
      setSessionHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRateSession = async (session, rating) => {
    if (!session.session_request_id) {
      console.error('Session request ID not found');
      setError('Session request ID is required to submit a rating');
      return;
    }
    
    if (!session.trainer_id) {
      console.error('Trainer ID not found in session');
      setError('Trainer ID is required to submit a rating');
      return;
    }
    
    try {
      console.log('Submitting rating:', { session_request_id: session.session_request_id, trainer_id: session.trainer_id, rating });
      const response = await apiService.submitSessionReview({
        session_request_id: session.session_request_id,
        trainer_id: session.trainer_id,
        rating: rating
      });
      
      console.log('Rating submission response:', response);
      
      if (response.Code === 200) {
        // Update the session in state with the new rating
        setSessionHistory(prev => prev.map(s => 
          s.id === session.id 
            ? { ...s, user_rating: rating }
            : s
        ));
      } else {
        setError(response.Message || 'Failed to submit rating');
      }
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.message || 'Failed to submit rating');
    }
  };
  
  const loadDeliverablesForSession = async (sessionRequestId) => {
    if (!sessionRequestId || sessionDeliverables[sessionRequestId]) {
      return; // Already loaded or no request ID
    }
    
    try {
      const deliverablesResponse = await apiService.getDeliverables(sessionRequestId);
      if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
        setSessionDeliverables(prev => ({
          ...prev,
          [sessionRequestId]: deliverablesResponse.data
        }));
      }
    } catch (err) {
      console.log(`No deliverables found for session request ${sessionRequestId}`);
    }
  };

  const handlePetUpdate = async (petId, updatedData) => {
    try {
      setLoading(true);
      console.log('Updating pet with data:', updatedData);
      
      // Map the local field names to API field names
      // The backend expects: age, race, pet_name, target_behaviors
      const apiData = {
        pet_name: updatedData.pet_name,
        age: parseInt(updatedData.age),
        race: updatedData.race,
        target_behaviors: updatedData.target_behaviors || ''
      };
      
      console.log('API payload:', apiData);
      const response = await apiService.updatePet(petId, apiData);
      console.log('Update response:', response);
      
      if (response.Code === 200) {
        // Reload pets after update
        await loadPets();
      } else {
        console.error('Update failed with response:', response);
        setError(response.Message || 'Failed to update pet');
      }
    } catch (err) {
      console.error('Error updating pet:', err);
      setError(err.message || 'Failed to update pet');
    } finally {
      setLoading(false);
    }
  };

  const handlePetDelete = async (petId) => {
    try {
      setLoading(true);
      await apiService.deletePet(petId);
      // Reload pets after deletion
      await loadPets();
    } catch (err) {
      console.error('Error deleting pet:', err);
      setError('Failed to delete pet');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPet = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Prepare data in the format expected by the API (matching PetSetup.js)
      const petData = {
        client_id: clientId,
        pet_name: newPetData.pet_name,
        pet_age: parseInt(newPetData.age),
        pet_breed: newPetData.race,
        target_behaviors: newPetData.target_behaviors
      };
      
      console.log('Adding pet with data:', petData);
      const response = await apiService.createPet(petData);
      console.log('Create pet response:', response);
      
      if (response.Code === 200 || response.status === 'success') {
        // Reset form and close modal
        setNewPetData({
          pet_name: '',
          age: '',
          race: '',
          target_behaviors: ''
        });
        setShowAddPetModal(false);
        // Reload pets
        await loadPets();
      } else {
        setError(response.Message || 'Failed to add pet. Please try again.');
      }
    } catch (err) {
      console.error('Error adding pet:', err);
      setError(err.message || 'Failed to add pet. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async (trainer) => {
    console.log('=== HANDLE START TRAINING ===');
    console.log('Current pets length:', pets.length);
    console.log('Current pets:', pets);
    
    setSelectedTrainer(trainer);
    setSelectedPetId(''); // Reset pet selection
    setRequestExplanation(''); // Reset explanation
    setRequestDuration(30);
    
    // Ensure pets are loaded before showing popup
    if (pets.length === 0) {
      console.log('No pets loaded, loading pets...');
      await loadPets();
      console.log('Pets loaded, new length:', pets.length);
    }
    
    setShowConfirmPopup(true);
  };

  const handleConfirmTraining = async () => {
    if (!selectedTrainer || !selectedPetId || !requestExplanation.trim()) {
      setError('Please select a pet and provide an explanation for the training request.');
      return;
    }
    
    try {
      setLoading(true);
      console.log('=== CREATING SESSION REQUEST ===');
      console.log('Client ID:', clientId);
      console.log('Trainer ID:', selectedTrainer.id);
      console.log('Pet ID:', selectedPetId);
      console.log('Explanation:', requestExplanation);
      console.log('Selected Trainer:', selectedTrainer);
      
      const response = await apiService.createSessionRequest({
        client_id: clientId,
        trainer_id: selectedTrainer.id,
        pet_id: selectedPetId,
        explanation: requestExplanation,
        duration_minutes: requestDuration
      });
      console.log('Session request created:', response);
      
      if (response.Code === 200) {
        // Reload active training requests to show the new request
        await loadActiveTrainingRequests();
        // Switch to requests tab to show the new request
        setActiveTab('requests');
        // Close popup
        setShowConfirmPopup(false);
        setSelectedTrainer(null);
        setSelectedPetId('');
        setRequestExplanation('');
        setRequestDuration(30);
        setError(null);
      } else {
        setError(`Error creating session request: ${response.Message}`);
      }
    } catch (err) {
      console.error('Error creating session request:', err);
      setError('Failed to create session request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTraining = () => {
    setShowConfirmPopup(false);
    setSelectedTrainer(null);
    setRequestDuration(30);
  };

  const handleCancelRequest = (request) => {
    setSelectedRequest(request);
    setShowCancelPopup(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return;
    
    try {
      setLoading(true);
      const response = await apiService.cancelSessionRequest(selectedRequest.id);
      console.log('Session request cancelled:', response);
      
      if (response.Code === 200) {
        // Reload active training requests to remove the cancelled request
        await loadActiveTrainingRequests();
        // Close popup
        setShowCancelPopup(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error('Error cancelling session request:', err);
      setError('Failed to cancel session request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCancel = () => {
    setShowCancelPopup(false);
    setSelectedRequest(null);
  };

  const handlePayForRequest = async (request, coupon = null) => {
    try {
      setLoading(true);
      setError(null);
      const checkoutData = { request_id: request.id };
      if (coupon && coupon.trim()) {
        checkoutData.coupon_code = coupon.trim();
      }
      const checkoutResponse = await apiService.createCheckoutSession(checkoutData);

      if (checkoutResponse.Code === 200) {
        if (checkoutResponse.mock) {
          await apiService.verifyCheckoutSession({
            session_id: checkoutResponse.session_id,
            request_id: request.id,
            amount: checkoutResponse.amount
          });
          await loadActiveTrainingRequests();
          alert('Payment successful! You can join the session once the trainer connects.');
          setShowCouponModal(false);
          setCouponCode('');
        } else if (checkoutResponse.url) {
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.url;
        } else {
          console.error('No URL in checkout response:', checkoutResponse);
          setError('Payment URL not received. Please try again.');
        }
      } else {
        setError(checkoutResponse.Message || 'Failed to initiate payment. Please try again.');
      }
    } catch (err) {
      console.error('Error initiating payment:', err);
      setError(err.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCouponModal = (request) => {
    setSelectedRequestForPayment(request);
    setCouponCode('');
    setShowCouponModal(true);
  };

  const handleApplyCouponAndPay = () => {
    if (selectedRequestForPayment) {
      handlePayForRequest(selectedRequestForPayment, couponCode);
    }
  };

  const handleJoinSession = async (requestId) => {
    try {
      setLoading(true);
      const response = await apiService.registerSessionJoin(requestId, 'client');
      console.log('Registered session join:', response);

      if (response.Code === 200) {
        window.location.href = `/video-call?request_id=${requestId}`;
      } else {
        setError(response.Message || 'Failed to join session');
      }
    } catch (err) {
      console.error('Error joining session:', err);
      setError('Failed to join session');
    } finally {
      setLoading(false);
    }
  };

  // Star rendering function for ratings
  const renderStars = (rating, size = 'normal') => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <span key={i} style={size === 'small' ? styles.starSmall : styles.star}>⭐</span>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <span key="half" style={size === 'small' ? styles.starSmall : styles.star}>✨</span>
      );
    }

    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <span key={`empty-${i}`} style={size === 'small' ? styles.starSmall : styles.star}>☆</span>
      );
    }

    return stars;
  };

  // PetCard component
  const PetCard = ({ pet, onUpdate, onDelete }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
      pet_name: pet.pet_name,
      age: pet.age,
      race: pet.race,
      target_behaviors: pet.target_behaviors || ''
    });

    const handleSave = () => {
      onUpdate(pet.id, editData);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditData({
        pet_name: pet.pet_name,
        age: pet.age,
        race: pet.race,
        target_behaviors: pet.target_behaviors || ''
      });
      setIsEditing(false);
    };

    return (
      <div style={styles.petCard}>
        <div style={styles.petCardHeader}>
          <div style={styles.petCardActions}>
            {!isEditing ? (
              <>
                <button 
                  style={styles.editButtonSmall}
                  onClick={() => setIsEditing(true)}
                  title="Edit"
                >
                  Edit
                </button>
                <button 
                  style={styles.deleteButtonSmall}
                  onClick={() => onDelete(pet.id)}
                  title="Delete"
                >
                  Delete
                </button>
              </>
            ) : (
              <>
                <button 
                  style={styles.saveButtonSmall}
                  onClick={handleSave}
                  title="Save"
                >
                  Save
                </button>
                <button 
                  style={styles.cancelButtonSmall}
                  onClick={handleCancel}
                  title="Cancel"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Pet Photo or Icon */}
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          {pet.photo_url ? (
            <img
              src={pet.photo_url}
              alt={pet.pet_name || 'Pet'}
              style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '3px solid #e67e22',
                margin: '0 auto'
              }}
            />
          ) : (
            <div style={styles.petIcon}>🐾</div>
          )}
        </div>

        {isEditing ? (
          <div style={styles.editFormCompact}>
            <div>
              <label style={styles.labelCompact}>Pet Name</label>
              <input
                type="text"
                value={editData.pet_name}
                onChange={(e) => setEditData({...editData, pet_name: e.target.value})}
                style={styles.inputCompact}
                placeholder="Name"
              />
            </div>
            <div>
              <label style={styles.labelCompact}>Age (years)</label>
              <input
                type="number"
                value={editData.age}
                onChange={(e) => setEditData({...editData, age: e.target.value})}
                style={styles.inputCompact}
                placeholder="Age"
                min="0"
                max="30"
              />
            </div>
            <div>
              <label style={styles.labelCompact}>Breed</label>
              <input
                type="text"
                value={editData.race}
                onChange={(e) => setEditData({...editData, race: e.target.value})}
                style={styles.inputCompact}
                placeholder="Breed"
              />
            </div>
            <div>
              <label style={styles.labelCompact}>Target Behaviors</label>
              <input
                type="text"
                value={editData.target_behaviors}
                onChange={(e) => setEditData({...editData, target_behaviors: e.target.value})}
                style={styles.inputCompact}
                placeholder="Target Behaviors"
              />
            </div>
          </div>
        ) : (
          <div style={styles.petCardInfo}>
            <div style={styles.petCardName}>{pet.pet_name}</div>
            <div style={styles.petCardDetails}>
              <div style={styles.petCardDetail}>{pet.age} years old</div>
              <div style={styles.petCardDetail}>{pet.race || 'Mixed'}</div>
            </div>
            {pet.target_behaviors && (
              <div style={styles.petCardBehaviors}>{pet.target_behaviors}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleTabChange = async (tabName) => {
    setActiveTab(tabName);
    setError(null);
    
    // Load data for the selected tab
    switch (tabName) {
      case 'find-trainer':
        console.log('=== SWITCHING TO FIND TRAINER TAB ===');
        console.log('Current activeTrainers state:', activeTrainers);
        console.log('Forcing refresh of active trainers...');
        await loadActiveTrainers();
        break;
      case 'pets':
        await loadPets();
        break;
      case 'profile':
        await loadProfile();
        break;
      case 'trainers':
        console.log('=== SWITCHING TO TRAINERS TAB ===');
        console.log('Current activeTrainers state:', activeTrainers);
        console.log('Forcing refresh of active trainers...');
        await loadActiveTrainers();
        break;
      case 'requests':
        await loadActiveTrainingRequests();
        break;
      case 'sessions':
        await loadSessionHistory();
        break;
      case 'tasks':
        await loadSessionHistory(); // Load sessions to get training tasks
        break;
      default:
        break;
    }
  };

  const renderPetsTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.petsHeader}>
        <h2 style={styles.tabTitle}>My Pets</h2>
        <button
          onClick={() => setShowAddPetModal(true)}
          style={styles.addPetButton}
        >
          + Add Pet
        </button>
      </div>
      {loading ? (
        <div style={styles.loading}>Loading pets...</div>
      ) : error ? (
        <div style={styles.error}>{error}</div>
      ) : pets.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>No Pets</div>
          <p>No pets registered yet</p>
        </div>
      ) : (
        <div style={styles.petsGrid}>
          {pets.map((pet, index) => (
            <PetCard 
              key={pet.id || index} 
              pet={pet} 
              onUpdate={handlePetUpdate}
              onDelete={handlePetDelete}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderProfileTab = () => {
    // Profile editing state is now managed at component level

    const handleEditChange = (e) => {
      const { name, value } = e.target;
      setProfileEditData(prev => ({
        ...prev,
        [name]: value
      }));
      if (name === 'password' || name === 'confirmPassword') {
        setPasswordError(null);
      }
    };

    const handleSave = async () => {
      try {
        setLoading(true);
        setError(null);
        setPasswordError(null);

        // Validate password if provided
        if (profileEditData.password) {
          if (profileEditData.password !== profileEditData.confirmPassword) {
            setPasswordError("Passwords do not match");
            return;
          }
        }

        const response = await apiService.updateClientProfile(clientId, profileEditData);
        if (response.Code === 200) {
          await loadProfile();
          setIsEditingProfile(false);
          setProfileEditData({
            first_name: '',
            last_name: '',
            phone_number: '',
            address: '',
            password: '',
            confirmPassword: ''
          });
        } else {
          setError(response.Message || 'Failed to update profile');
        }
      } catch (err) {
        console.error('Error updating profile:', err);
        setError('Failed to update profile');
      } finally {
        setLoading(false);
      }
    };

    const handleCancel = () => {
      setIsEditingProfile(false);
      setProfileEditData({
        first_name: '',
        last_name: '',
        phone_number: '',
        address: '',
        password: '',
        confirmPassword: ''
      });
      setPasswordError(null);
    };

    return (
      <div style={styles.tabContent}>
        <div style={styles.tabHeader}>
          <h2 style={styles.tabTitle}>My Profile</h2>
          {!isEditingProfile && (
            <button
              onClick={() => setIsEditingProfile(true)}
              style={styles.editButton}
            >
              Edit Profile
            </button>
          )}
        </div>
        {loading ? (
          <div style={styles.loading}>Loading profile...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : !profile ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>Profile</div>
            <p>Profile information not available</p>
          </div>
        ) : isEditingProfile ? (
          <div style={styles.editForm}>
            {/* Profile Photo Upload Section */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              {profile.profile_photo_url ? (
                <img
                  src={profile.profile_photo_url}
                  alt="Profile"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #e67e22',
                    marginBottom: '15px'
                  }}
                />
              ) : (
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundColor: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  color: '#64748b',
                  margin: '0 auto 15px',
                  border: '3px solid #475569'
                }}>
                  👤
                </div>
              )}
              <div>
                <label style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'linear-gradient(45deg, #e67e22, #f39c12)',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  opacity: uploadingPhoto ? 0.6 : 1
                }}>
                  {uploadingPhoto ? 'Uploading...' : '📷 Change Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleClientPhotoUpload}
                    disabled={uploadingPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>First Name</label>
              <input
                type="text"
                name="first_name"
                value={profileEditData.first_name || profile.first_name}
                onChange={handleEditChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={profileEditData.last_name || profile.last_name}
                onChange={handleEditChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                name="phone_number"
                value={profileEditData.phone_number || profile.phone_number}
                onChange={handleEditChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                name="address"
                value={profileEditData.address || profile.address}
                onChange={handleEditChange}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>New Password (optional)</label>
              <input
                type="password"
                name="password"
                value={profileEditData.password}
                onChange={handleEditChange}
                style={styles.input}
                placeholder="Leave blank to keep current password"
              />
            </div>
            {profileEditData.password && (
              <div style={styles.inputGroup}>
                <label style={styles.label}>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={profileEditData.confirmPassword}
                  onChange={handleEditChange}
                  style={styles.input}
                />
              </div>
            )}
            {passwordError && <div style={styles.error}>{passwordError}</div>}
            <div style={styles.formActions}>
              <button onClick={handleSave} style={styles.saveButton}>
                Save Changes
              </button>
              <button onClick={handleCancel} style={styles.cancelButton}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.profileCard}>
            {/* Profile Photo Section */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              {profile.profile_photo_url ? (
                <img
                  src={profile.profile_photo_url}
                  alt="Profile"
                  style={{
                    width: '150px',
                    height: '150px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid #e67e22',
                    marginBottom: '15px'
                  }}
                />
              ) : (
                <div style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  backgroundColor: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  color: '#64748b',
                  margin: '0 auto 15px',
                  border: '4px solid #475569'
                }}>
                  👤
                </div>
              )}
              <div>
                <label style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'linear-gradient(45deg, #e67e22, #f39c12)',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  opacity: uploadingPhoto ? 0.6 : 1
                }}>
                  {uploadingPhoto ? 'Uploading...' : '📷 Upload Photo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleClientPhotoUpload}
                    disabled={uploadingPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>

            <div style={styles.profileIcon}>Profile</div>
            <div style={styles.profileInfo}>
              <h3 style={styles.profileInfoTitle}>{profile.first_name} {profile.last_name}</h3>
              <p style={styles.profileInfoText}><strong>Email:</strong> {profile.email}</p>
              <p style={styles.profileInfoText}><strong>Phone:</strong> {profile.phone_number || 'Not provided'}</p>
              <p style={styles.profileInfoText}><strong>Address:</strong> {profile.address || 'Not provided'}</p>
              {profile.favorite_trainer && (
                <p style={styles.profileInfoText}><strong>Favorite Trainer:</strong> {profile.favorite_trainer.name}</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTrainersTab = () => {
    console.log('renderTrainersTab called - activeTrainers state:', activeTrainers);
    console.log('activeTrainers.length:', activeTrainers.length);
    console.log('loading:', loading);
    console.log('error:', error);
    
    const philosophyOptions = [
      'Force Free / Pure Positive',
      'Positive Reinforcement Based (+R)',
      'Clicker / Marker Training',
      'Fear Free',
      'Science Based',
      'Relationship Based',
      'Balanced',
      'LIMA (Least Intrusive, Minimally Aversive)',
      'Traditional Training Methods',
      'Alpha / Pack Based',
      'Remote Training'
    ];

    const expertiseOptions = [
      'Puppy',
      'Manners',
      'Behavior Modification',
      'Aggression Management – Dog to dog',
      'Aggression Management - human directed',
      'Reactivity',
      'Separation Anxiety',
      'Anxiety / Fears / Phobias',
      'Leash Walking',
      'Potty Training',
      'Rescue Rehab',
      'Service Dog',
      'Therapy Dog',
      'Protection',
      'Tracking',
      'Competition Obedience',
      'Scentwork',
      'Agility',
      'Conformation',
      'Herding',
      'Earthdog'
    ];
    
    return (
      <div style={styles.tabContent}>
        <div style={styles.tabHeader}>
          <h2 style={styles.tabTitle}>Active Trainers</h2>
          <button
            onClick={loadActiveTrainers}
            style={{
              ...styles.refreshButton,
              ...(refreshSuccess ? styles.refreshButtonSuccess : {}),
              ...(loading ? styles.refreshButtonLoading : {})
            }}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : refreshSuccess ? 'Refreshed!' : 'Refresh'}
          </button>
        </div>

        {/* Filters Section */}
        {!loading && activeTrainers.length > 0 && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.06)',
            borderRadius: '8px',
            padding: '8px 12px',
            marginBottom: '16px',
            border: '1px solid rgba(255, 255, 255, 0.12)'
          }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center'
            }}>
              {/* Search */}
              <input
                type="text"
                value={trainerFilters.search}
                onChange={(e) => setTrainerFilters({...trainerFilters, search: e.target.value})}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '0.85rem',
                  minWidth: '140px',
                  flex: '0 1 200px'
                }}
                placeholder="Search..."
              />

              {/* Training Philosophy - Custom Dropdown */}
              <div style={{ position: 'relative', flex: '0 1 220px', minWidth: '180px' }} data-dropdown>
                <button
                  onClick={() => toggleDropdown('philosophy')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'white',
                    color: '#1a202c',
                    fontSize: '0.85rem',
                    width: '100%',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <span>Training Philosophy {trainerFilters.philosophy.length > 0 ? `(${trainerFilters.philosophy.length})` : ''}</span>
                  <span>{openDropdowns.philosophy ? '▲' : '▼'}</span>
                </button>
                {openDropdowns.philosophy && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}>
                    {philosophyOptions.map(phil => (
                      <label
                        key={phil}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                          color: '#1a202c',
                          fontSize: '0.85rem',
                          background: 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.philosophy.includes(phil)}
                          onChange={() => handleFilterToggle('philosophy', phil)}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span>{phil}</span>
                      </label>
                    ))}
                  </div>
                )}
                {trainerFilters.philosophy.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {trainerFilters.philosophy.map(phil => (
                      <span
                        key={phil}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        {phil}
                        <button
                          onClick={() => removeFilter('philosophy', phil)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            padding: '0',
                            lineHeight: '1',
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Area of Expertise - Custom Dropdown */}
              <div style={{ position: 'relative', flex: '0 1 220px', minWidth: '180px' }} data-dropdown>
                <button
                  onClick={() => toggleDropdown('expertise')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'white',
                    color: '#1a202c',
                    fontSize: '0.85rem',
                    width: '100%',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <span>Area of Expertise {trainerFilters.expertise.length > 0 ? `(${trainerFilters.expertise.length})` : ''}</span>
                  <span>{openDropdowns.expertise ? '▲' : '▼'}</span>
                </button>
                {openDropdowns.expertise && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}>
                    {expertiseOptions.map(exp => (
                      <label
                        key={exp}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                          color: '#1a202c',
                          fontSize: '0.85rem',
                          background: 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.expertise.includes(exp)}
                          onChange={() => handleFilterToggle('expertise', exp)}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span>{exp}</span>
                      </label>
                    ))}
                  </div>
                )}
                {trainerFilters.expertise.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {trainerFilters.expertise.map(exp => (
                      <span
                        key={exp}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        {exp}
                        <button
                          onClick={() => removeFilter('expertise', exp)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            padding: '0',
                            lineHeight: '1',
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Credentials - Custom Dropdown */}
              <div style={{ position: 'relative', flex: '0 1 180px', minWidth: '150px' }} data-dropdown>
                <button
                  onClick={() => toggleDropdown('credentials')}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'white',
                    color: '#1a202c',
                    fontSize: '0.85rem',
                    width: '100%',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  <span>Credentials {trainerFilters.credentials.length > 0 ? `(${trainerFilters.credentials.length})` : ''}</span>
                  <span>{openDropdowns.credentials ? '▲' : '▼'}</span>
                </button>
                {openDropdowns.credentials && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '6px',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}>
                    {['CDT', 'CPDT', 'CPDT-KA', 'KPA-CTP', 'CBCC-KA', 'CCPDT', 'IAABC', 'VSPDT', 'ABC', 'Other'].map(cred => (
                      <label
                        key={cred}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                          color: '#1a202c',
                          fontSize: '0.85rem',
                          background: 'white'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        <input
                          type="checkbox"
                          checked={trainerFilters.credentials.includes(cred)}
                          onChange={() => handleFilterToggle('credentials', cred)}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        <span>{cred}</span>
                      </label>
                    ))}
                  </div>
                )}
                {trainerFilters.credentials.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    {trainerFilters.credentials.map(cred => (
                      <span
                        key={cred}
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        {cred}
                        <button
                          onClick={() => removeFilter('credentials', cred)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            padding: '0',
                            lineHeight: '1',
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>Loading trainers...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : activeTrainers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>👨‍🏫</div>
            <p>No active trainers available</p>
          </div>
        ) : filteredTrainers.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🔍</div>
            <p>No trainers found matching your filters</p>
          </div>
        ) : (
        <div style={styles.trainersGrid}>
          {filteredTrainers.map(trainer => (
            <div key={trainer.id} style={styles.trainerCard}>
              {/* Trainer Header */}
              <div style={styles.trainerHeader}>
                <div style={styles.avatar}>{trainer.photo}</div>
                <div style={styles.trainerInfo}>
                  <div style={styles.name}>{trainer.name}</div>
                  <div style={styles.experience}>{trainer.experience} experience</div>
                </div>
                <div style={{
                  ...styles.onlineStatus,
                  ...(trainer.isOnline ? styles.onlineStatusOnline : styles.onlineStatusOffline)
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: trainer.isOnline ? '#27ae60' : '#e53e3e'
                  }}></div>
                  {trainer.isOnline ? 'Online' : 'Offline'}
                </div>
              </div>

              {/* Rating */}
              <div style={styles.rating}>
                {renderStars(trainer.overallRating)}
                <span style={styles.ratingText}>{trainer.overallRating}</span>
                <span style={styles.reviewCount}>({trainer.totalReviews} reviews)</span>
              </div>

              {/* Pricing */}
              <div style={styles.pricing}>
                <div style={styles.priceTitle}>⚡ Live Session Pricing</div>
                <div style={styles.priceDetails}>
                  <strong>${trainer.baseRate}</strong> first 10 minutes + <strong>${trainer.perMinuteRate}/min</strong> after<br/>
                  <em>Instant connection - start training immediately!</em>
                </div>
              </div>

              {/* Bio */}
              <div style={styles.bio}>{trainer.bio}</div>

              {/* Specializations */}
              {trainer.specializations && trainer.specializations.length > 0 && (
                <div style={styles.specializations}>
                  <div style={styles.sectionTitle}>Specializations:</div>
                  <div style={styles.tags}>
                    {trainer.specializations.map((spec, index) => (
                      <span key={index} style={styles.tag}>{spec}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Training Philosophy */}
              {trainer.philosophy && trainer.philosophy.length > 0 && (
                <div style={styles.specializations}>
                  <div style={styles.sectionTitle}>Training Philosophy:</div>
                  <div style={styles.tags}>
                    {trainer.philosophy.map((phil, index) => (
                      <span key={index} style={styles.tag}>{phil}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={styles.actions}>
                <button
                  style={{
                    ...styles.startBtn,
                    ...(trainer.isOnline ? {} : styles.startBtnDisabled)
                  }}
                  disabled={!trainer.isOnline}
                  onClick={() => trainer.isOnline && handleStartTraining(trainer)}
                >
                  {trainer.isOnline ? '🚀 Start Training Now' : '⏳ Currently Offline'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderRequestsTab = () => (
    <div style={styles.tabContent}>
      <div style={styles.tabHeader}>
        <h2 style={styles.tabTitle}>Active Training Requests</h2>
        <button
          onClick={loadActiveTrainingRequests}
          style={{
            ...styles.refreshButton,
            ...(refreshSuccess ? styles.refreshButtonSuccess : {}),
            ...(loading ? styles.refreshButtonLoading : {})
          }}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : refreshSuccess ? 'Refreshed!' : 'Refresh'}
        </button>
      </div>
      {loading ? (
        <div style={styles.loading}>Loading training requests...</div>
      ) : error ? (
        <div style={styles.error}>{error}</div>
      ) : activeTrainingRequests.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>No Requests</div>
          <p>No active training requests</p>
          <p>Go to "Active Trainers" to start a training session!</p>
        </div>
      ) : (
        <div style={styles.requestsList}>
          {activeTrainingRequests.map(request => (
            <div key={request.id} style={styles.requestCard}>
              <div style={styles.requestHeader}>
                <div style={styles.trainerInfo}>
                  <div style={styles.trainerName}>{request.trainer_name}</div>
                    <div style={styles.requestStatus}>
                      {(() => {
                        const statusLabels = {
                          pending: 'Waiting for Trainer',
                          awaiting_payment: 'Awaiting Your Payment',
                          accepted: 'Ready to Join',
                          active: 'In Progress',
                          completed: 'Completed'
                        };
                        const statusColors = {
                          pending: '#f39c12',
                          awaiting_payment: '#f39c12',
                          accepted: '#3498db',
                          active: '#27ae60',
                          completed: '#27ae60',
                          cancelled: '#e74c3c'
                        };
                        const label = statusLabels[request.status] || request.status;
                        const color = statusColors[request.status] || '#64748b';
                        return (
                          <>
                            Status:{' '}
                            <span style={{ color, fontWeight: 600 }}>
                              {label}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                </div>
                <div style={styles.requestTime}>
                  {new Date(request.created_at).toLocaleString()}
                </div>
              </div>
              
              <div style={styles.requestDetails}>
                <div style={styles.detailRow}>
                  <strong>Session ID:</strong> {request.id}
                </div>
                  {request.duration_minutes && (
                    <div style={styles.detailRow}>
                      <strong>Duration:</strong> {request.duration_minutes} minutes
                    </div>
                  )}
                <div style={styles.detailRow}>
                    <strong>Trainer Rate:</strong> ${request.first_10_min_rate?.toFixed ? request.first_10_min_rate.toFixed(2) : Number(request.first_10_min_rate || 0).toFixed(2)} first 10 min + ${Number(request.per_minute_rate || 0).toFixed(2)}/min after
                  </div>
                  {request.total_price !== null && request.total_price !== undefined && (
                    <div style={styles.detailRow}>
                      <strong>Estimated Session Cost:</strong> {formatCurrency(request.total_price)}
                    </div>
                  )}
                {request.notes && (
                  <div style={styles.detailRow}>
                    <strong>Notes:</strong> {request.notes}
                  </div>
                )}
              </div>

              <div style={styles.requestActions}>
                  {(request.status === 'awaiting_payment' || (request.status === 'pending' && request.payment_status !== 'completed')) && request.payment_status !== 'completed' && (
                    <button
                      style={styles.payBtn}
                      onClick={() => handleOpenCouponModal(request)}
                      disabled={loading || request.payment_status === 'completed'}
                    >
                      💳 Pay {formatCurrency(request.total_price || 0)}
                    </button>
                  )}
                  {request.payment_status === 'completed' && (
                    <button
                      style={styles.payBtnDisabled}
                      disabled
                    >
                      ✅ Payment Complete
                    </button>
                  )}
                  {request.payment_status === 'completed' ? (
                    <button
                      style={styles.joinBtn}
                      onClick={() => handleJoinSession(request.id)}
                      disabled={loading}
                    >
                      🚀 Join Session Now
                    </button>
                  ) : (request.status === 'accepted' || request.status === 'active') ? (
                    <button
                      style={styles.joinBtn}
                      onClick={() => handleJoinSession(request.id)}
                      disabled={loading}
                    >
                      🚀 Join Session
                    </button>
                  ) : request.status === 'pending' && request.payment_status !== 'completed' ? (
                    <button
                      style={styles.waitingForTrainerBtn}
                      disabled
                    >
                      ⏰ Waiting for Trainer to Accept
                    </button>
                  ) : request.status !== 'awaiting_payment' && request.status !== 'pending' ? (
                    <button
                      style={styles.joinBtnDisabled}
                      disabled
                    >
                      ⏳ Unavailable
                    </button>
                  ) : null}
                <button
                  style={styles.cancelBtn}
                  onClick={() => handleCancelRequest(request)}
                    disabled={loading}
                >
                  Cancel Request
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleViewSessionDetails = async (session) => {
    // Load deliverables if not already loaded
    if (session.session_request_id) {
      await loadDeliverablesForSession(session.session_request_id);
    }
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const renderSessionsTab = () => {
    return (
      <div style={styles.tabContent}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={styles.tabTitle}>Session History</h2>
          <button
            onClick={loadSessionHistory}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(45deg, #e67e22, #f39c12)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🔄 Refresh
          </button>
        </div>
        {loading ? (
          <div style={styles.loading}>Loading sessions...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : sessionHistory.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>No Sessions</div>
            <p>No sessions completed yet</p>
            <p style={styles.emptySubtext}>Book a session with an active trainer to get started!</p>
          </div>
        ) : (
          <div style={styles.sessionsList}>
            {sessionHistory.map((session) => {
              const sessionDate = session.date ? new Date(session.date) : null;
              const sessionEndDate = session.end_date ? new Date(session.end_date) : null;
              const formattedDate = sessionDate ? sessionDate.toLocaleDateString() : 'N/A';
              const formattedTime = sessionDate ? sessionDate.toLocaleTimeString() : 'N/A';
              const formattedEndTime = sessionEndDate ? sessionEndDate.toLocaleTimeString() : null;
              
              return (
                <div key={session.id} style={styles.historyCard}>
                  <div style={styles.historyCardContent}>
                    <div style={styles.historyCardLeft}>
                      <div style={styles.historyCardTitle}>Session #{session.id.slice(-8)}</div>
                      <div style={styles.historyCardDate}>{formattedDate}</div>
                    </div>
                    <div style={styles.historyCardInfoRow}>
                      <span style={styles.historyCardLabel}>Trainer: </span>
                      <span style={styles.historyCardValue}>{session.trainer_name}</span>
                      <span style={styles.historyCardSpacer}> | </span>
                      <span style={styles.historyCardLabel}>Time: </span>
                      <span style={styles.historyCardValue}>{formattedTime}{formattedEndTime ? ` – ${formattedEndTime}` : ''}</span>
                      <span style={styles.historyCardSpacer}> | </span>
                      <span style={styles.historyCardLabel}>Duration: </span>
                      <span style={styles.historyCardValue}>{session.duration_minutes || session.session_duration_minutes || 0} minutes</span>
                      <span style={styles.historyCardSpacer}> | </span>
                      <span style={styles.historyCardLabel}>Price: </span>
                      <span style={styles.historyCardValue}>{formatCurrency(session.price)}</span>
                      {session.status && (
                        <>
                          <span style={styles.historyCardSpacer}> | </span>
                          <span style={styles.historyCardLabel}>Status: </span>
                          <span style={{
                            ...styles.historyCardValue,
                            color: session.status === 'completed' ? '#27ae60' : 
                                   session.status === 'in_progress' ? '#f39c12' : '#e74c3c'
                          }}>{session.status}</span>
                        </>
                      )}
                    </div>
                    <div style={styles.historyActions}>
                      {/* Star Rating Component */}
                      <div style={styles.ratingContainer}>
                        <span style={styles.ratingLabel}>Rate this session: </span>
                        <div style={styles.starRating}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              onClick={() => handleRateSession(session, star)}
                              style={{
                                ...styles.star,
                                color: session.user_rating && star <= session.user_rating ? '#f39c12' : '#64748b',
                                cursor: 'pointer'
                              }}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        {session.user_rating && (
                          <span style={styles.ratingText}>{session.user_rating}/5</span>
                        )}
                      </div>
                      <span style={styles.historyStatus}>Completed</span>
                      <button 
                        style={styles.deliverablesButton}
                        onClick={() => handleViewSessionDetails(session)}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Session Details Modal */}
        {showSessionModal && selectedSession && (
          <div style={styles.modalOverlay} onClick={() => setShowSessionModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>Session Details</h2>
                <button style={styles.modalCloseButton} onClick={() => setShowSessionModal(false)}>×</button>
              </div>
              
              <div style={styles.modalBody}>
                <div style={styles.modalSessionInfo}>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Session ID: </span>
                    <span style={styles.modalInfoValue}>#{selectedSession.id.slice(-8)}</span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Date: </span>
                    <span style={styles.modalInfoValue}>
                      {selectedSession.date ? new Date(selectedSession.date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Time: </span>
                    <span style={styles.modalInfoValue}>
                      {selectedSession.date ? new Date(selectedSession.date).toLocaleTimeString() : 'N/A'}
                      {selectedSession.end_date ? ` – ${new Date(selectedSession.end_date).toLocaleTimeString()}` : ''}
                    </span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Trainer: </span>
                    <span style={styles.modalInfoValue}>{selectedSession.trainer_name}</span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Duration: </span>
                    <span style={styles.modalInfoValue}>{selectedSession.duration_minutes || selectedSession.session_duration_minutes || 0} minutes</span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Price: </span>
                    <span style={styles.modalInfoValue}>{formatCurrency(selectedSession.price)}</span>
                  </div>
                  <div style={styles.modalInfoRow}>
                    <span style={styles.modalInfoLabel}>Status: </span>
                    <span style={{
                      ...styles.modalInfoValue,
                      color: selectedSession.status === 'completed' ? '#27ae60' : 
                             selectedSession.status === 'in_progress' ? '#f39c12' : '#e74c3c'
                    }}>{selectedSession.status}</span>
                  </div>
                </div>
                
                {/* Deliverables from trainer */}
                {selectedSession.session_request_id && sessionDeliverables[selectedSession.session_request_id] && (
                  <>
                    {sessionDeliverables[selectedSession.session_request_id].summary && (
                      <div style={styles.modalSection}>
                        <h3 style={styles.modalSectionTitle}>Session Summary</h3>
                        <p style={styles.modalSectionText}>{sessionDeliverables[selectedSession.session_request_id].summary}</p>
                      </div>
                    )}
                    
                    {sessionDeliverables[selectedSession.session_request_id].checklist_items && 
                     sessionDeliverables[selectedSession.session_request_id].checklist_items.length > 0 && (
                      <div style={styles.modalSection}>
                        <h3 style={styles.modalSectionTitle}>Training Checklist</h3>
                        <div style={styles.modalChecklist}>
                          {sessionDeliverables[selectedSession.session_request_id].checklist_items.map((item, itemIndex) => (
                            <div key={itemIndex} style={styles.modalChecklistItem}>
                              <span style={styles.modalChecklistNumber}>{itemIndex + 1}.</span>
                              <span style={styles.modalChecklistText}>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Fallback to session summary if no deliverables */}
                {(!selectedSession.session_request_id || !sessionDeliverables[selectedSession.session_request_id]) && selectedSession.summary && (
                  <div style={styles.modalSection}>
                    <h3 style={styles.modalSectionTitle}>Session Summary</h3>
                    <p style={styles.modalSectionText}>{selectedSession.summary}</p>
                  </div>
                )}
                
                {/* Show message if no deliverables yet */}
                {selectedSession.status === 'completed' && selectedSession.session_request_id && !sessionDeliverables[selectedSession.session_request_id] && (
                  <div style={styles.modalSection}>
                    <p style={styles.modalNoDeliverables}>
                      Deliverables are pending. Your trainer will add a summary and checklist soon.
                    </p>
                  </div>
                )}
              </div>
              
              <div style={styles.modalFooter}>
                <button style={styles.modalCloseBtn} onClick={() => setShowSessionModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTasksTab = () => {
    // Collect all training tasks from all sessions
    const allTasks = [];
    sessionHistory.forEach(session => {
      const tasks = normalizeTasks(session.training_tasks);
      tasks.forEach((task, index) => {
        allTasks.push({
          ...task,
          sessionId: session.id,
          sessionDate: session.date,
          trainerName: session.trainer_name,
          id: `${session.id}-${index}`
        });
      });
    });

    return (
      <div style={styles.tabContent}>
        <h2 style={styles.tabTitle}>Training Tasks</h2>
        {loading ? (
          <div style={styles.loading}>Loading tasks...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : allTasks.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>No Tasks</div>
            <p>No training tasks available yet</p>
            <p style={styles.emptySubtext}>Complete a training session to receive tasks from your trainer!</p>
          </div>
        ) : (
          <div style={styles.tasksGrid}>
            {allTasks.map((task) => (
              <div key={task.id} style={styles.taskCard}>
                <div style={styles.taskHeader}>
                  <h3 style={styles.taskTitle}>{task.description}</h3>
                  <div style={styles.taskMeta}>
                    <span style={styles.taskSession}>Session #{task.sessionId.slice(-8)}</span>
                    <span style={styles.taskDate}>{new Date(task.sessionDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <div style={styles.taskDetails}>
                  <p style={styles.taskTrainer}>Trainer: {task.trainerName}</p>
                  {task.notes && (
                    <p style={styles.taskNotes}>{task.notes}</p>
                  )}
                </div>
                <div style={styles.taskStatus}>
                  <input
                    type="checkbox"
                    checked={task.completed || false}
                    onChange={() => {}} // Read-only for now
                    style={styles.taskCheckbox}
                  />
                  <span style={styles.taskStatusText}>
                    {task.completed ? 'Completed' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const styles = {
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: '#0f172a',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    },
    topNav: {
      background: '#1e293b',
      boxShadow: '0 1px 0 rgba(0, 0, 0, 0.2)',
      borderBottom: '1px solid #334155',
      position: 'sticky',
      top: 0,
      zIndex: 100
    },
    navHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 24px',
      borderBottom: 'none'
    },
    navActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    userInfo: {
      color: '#6b7280',
      fontSize: '0.875rem',
      fontWeight: '400',
      display: 'none'
    },
    horizontalTabList: {
      display: 'flex',
      padding: '0 24px',
      gap: 0,
      overflowX: 'auto',
      background: '#1e293b'
    },
    horizontalTabButton: {
      padding: '10px 20px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#94a3b8',
      whiteSpace: 'nowrap',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s ease',
      borderRadius: '6px 6px 0 0',
      marginRight: '4px'
    },
    activeHorizontalTabButton: {
      color: '#ffffff',
      borderBottomColor: '#3b82f6',
      fontWeight: '600',
      background: 'rgba(59, 130, 246, 0.1)'
    },
    highlightedTabButton: {
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important',
      color: 'white !important',
      fontWeight: '600',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
      border: 'none',
      borderBottom: '2px solid #2563eb'
    },
    dropdownContainer: {
      position: 'relative',
      display: 'inline-block'
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      background: '#334155',
      border: '1px solid #475569',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      minWidth: '200px',
      zIndex: 100,
      marginTop: '4px'
    },
    dropdownItem: {
      width: '100%',
      padding: '12px 16px',
      background: 'transparent',
      border: 'none',
      color: '#cbd5e1',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'all 0.15s ease',
      borderBottom: '1px solid #475569'
    },
    dropdownItemHover: {
      background: '#3b82f6',
      color: 'white'
    },
    sidebar: {
      width: '250px',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
      padding: '20px 0',
      display: 'flex',
      flexDirection: 'column'
    },
    logo: {
      color: '#f1f5f9',
      fontSize: '0.9375rem',
      fontWeight: '600',
      letterSpacing: '-0.01em',
      padding: 0
    },
    tabList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      flex: 1
    },
    tabItem: {
      margin: '5px 0'
    },
    tabButton: {
      width: '100%',
      padding: '15px 20px',
      border: 'none',
      background: 'transparent',
      textAlign: 'left',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#64748b',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    activeTabButton: {
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      fontWeight: '600'
    },
    logoutButton: {
      margin: 0,
      padding: '6px 14px',
      background: 'transparent',
      color: '#94a3b8',
      border: '1px solid #334155',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: '500',
      transition: 'all 0.15s ease'
    },
    mainContent: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
      background: '#0f172a'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px'
    },
    welcomeTitle: {
      color: 'white',
      fontSize: '2rem',
      fontWeight: '700',
      margin: 0
    },
    homeButton: {
      padding: '12px 24px',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '600',
      transition: 'all 0.3s ease'
    },
    tabContent: {
      background: '#1e293b',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
      border: '1px solid #334155',
      minHeight: '400px'
    },
    tabTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#f1f5f9',
      marginBottom: 0,
      borderBottom: 'none',
      paddingBottom: 0,
      letterSpacing: '-0.01em'
    },
    petsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    addPetButton: {
      padding: '12px 24px',
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
      transition: 'all 0.2s ease'
    },
    tabHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      borderBottom: 'none',
      paddingBottom: 0
    },
    refreshButton: {
      padding: '6px 12px',
      background: '#334155',
      color: '#cbd5e1',
      border: '1px solid #475569',
      borderRadius: '6px',
      transition: 'all 0.3s ease'
    },
    refreshButtonSuccess: {
      background: '#22c55e',
      color: 'white',
      border: '1px solid #16a34a'
    },
    refreshButtonLoading: {
      background: '#475569',
      cursor: 'wait'
    },
    loading: {
      textAlign: 'center',
      fontSize: '0.9375rem',
      color: '#94a3b8',
      padding: '48px'
    },
    error: {
      textAlign: 'center',
      fontSize: '0.9375rem',
      color: '#ef4444',
      padding: '24px',
      backgroundColor: 'rgba(220, 38, 38, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#7f8c8d'
    },
    emptyIcon: {
      fontSize: '4rem',
      marginBottom: '20px'
    },
    emptySubtext: {
      fontSize: '0.9rem',
      marginTop: '10px',
      color: '#9ca3af'
    },
    petsScrollContainer: {
      maxHeight: '500px',
      overflowY: 'auto',
      paddingRight: '10px'
    },
    petsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '16px',
      maxHeight: '600px',
      overflowY: 'auto',
      paddingRight: '8px'
    },
    petCard: {
      background: '#334155',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #475569',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.2s ease',
      position: 'relative',
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column'
    },
    petCardHeader: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },
    petIcon: {
      fontSize: '3rem',
      textAlign: 'center',
      marginBottom: '12px'
    },
    petCardActions: {
      display: 'flex',
      gap: '4px'
    },
    editButtonSmall: {
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      lineHeight: 1
    },
    deleteButtonSmall: {
      background: '#ef4444',
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      lineHeight: 1
    },
    saveButtonSmall: {
      background: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      lineHeight: 1
    },
    cancelButtonSmall: {
      background: '#64748b',
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      lineHeight: 1
    },
    petCardInfo: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    petCardName: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#f1f5f9',
      marginBottom: '8px',
      textAlign: 'center'
    },
    petCardDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    },
    petCardDetail: {
      fontSize: '0.875rem',
      color: '#cbd5e1',
      textAlign: 'center'
    },
    petCardBehaviors: {
      fontSize: '0.75rem',
      color: '#94a3b8',
      textAlign: 'center',
      marginTop: '8px',
      fontStyle: 'italic'
    },
    editFormCompact: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    inputCompact: {
      padding: '8px',
      border: '1px solid #475569',
      borderRadius: '6px',
      fontSize: '0.875rem',
      background: '#1e293b',
      color: '#f1f5f9'
    },
    labelCompact: {
      display: 'block',
      width: '100%',
      fontSize: '0.75rem',
      color: '#94a3b8',
      marginBottom: '4px',
      fontWeight: '500'
    },
    petActions: {
      display: 'flex',
      gap: '8px'
    },
    editButton: {
      background: '#3498db',
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    deleteButton: {
      background: '#e74c3c',
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    saveButton: {
      background: '#27ae60',
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    cancelButton: {
      background: '#95a5a6',
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    editForm: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    petInfo: {
      textAlign: 'center'
    },
    petName: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px'
    },
    petDetails: {
      fontSize: '0.95rem',
      color: '#64748b'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#2c3e50'
    },
    input: {
      padding: '8px 12px',
      border: '2px solid #e2e8f0',
      borderRadius: '6px',
      fontSize: '0.9rem'
    },
    profileCard: {
      background: '#f8f9fa',
      borderRadius: '12px',
      padding: '30px',
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      border: '2px solid #e2e8f0'
    },
    profileIcon: {
      fontSize: '4rem',
      color: '#e67e22'
    },
    profileInfo: {
      flex: 1
    },
    profileInfoTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px'
    },
    profileInfoText: {
      fontSize: '1rem',
      color: '#64748b',
      marginBottom: '8px'
    },
    trainersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
      gap: '16px',
      maxHeight: '600px',
      overflowY: 'auto',
      paddingRight: '8px'
    },
    trainerCard: {
      background: '#334155',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid #475569',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      transition: 'all 0.2s ease',
      position: 'relative'
    },
    trainerHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
      gap: '12px'
    },
    avatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '1.5rem',
      fontWeight: 'bold',
      flexShrink: 0
    },
    trainerInfo: {
      flex: 1,
      minWidth: 0
    },
    name: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: 'white',
      marginBottom: '4px'
    },
    trainerTitle: {
      fontSize: '0.9rem',
      color: '#718096',
      marginBottom: '2px'
    },
    experience: {
      fontSize: '0.85rem',
      color: 'white'
    },
    onlineStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '0.8rem',
      fontWeight: '600',
      padding: '4px 8px',
      borderRadius: '12px',
      flexShrink: 0
    },
    onlineStatusOnline: {
      background: '#f0fff4',
      color: '#22543d'
    },
    onlineStatusOffline: {
      background: '#fed7d7',
      color: '#742a2a'
    },
    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '16px'
    },
    star: {
      fontSize: '1.2rem'
    },
    starSmall: {
      fontSize: '0.9rem'
    },
    ratingText: {
      fontSize: '1rem',
      fontWeight: '600',
      color: 'white'
    },
    reviewCount: {
      fontSize: '0.85rem',
      color: 'white'
    },
    pricing: {
      background: '#f7fafc',
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '16px'
    },
    priceTitle: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '4px'
    },
    priceDetails: {
      fontSize: '0.85rem',
      color: '#4a5568',
      lineHeight: '1.4'
    },
    bio: {
      fontSize: '0.9rem',
      color: 'white',
      lineHeight: '1.5',
      marginBottom: '16px'
    },
    specializations: {
      marginBottom: '16px'
    },
    sectionTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: 'white',
      marginBottom: '8px'
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px'
    },
    tag: {
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      fontWeight: '500'
    },
    actions: {
      marginTop: '20px',
      paddingTop: '16px',
      borderTop: '1px solid #e2e8f0'
    },
    startBtn: {
      width: '100%',
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.15s ease'
    },
    startBtnDisabled: {
      background: '#334155',
      color: '#64748b',
      cursor: 'not-allowed'
    },
    sessionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    historyCard: {
      background: '#334155',
      borderRadius: '8px',
      padding: '15px 20px',
      border: '1px solid #475569',
      marginBottom: '12px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
    },
    historyCardContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      width: '100%'
    },
    historyCardLeft: {
      minWidth: '150px'
    },
    historyCardTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#f1f5f9',
      marginBottom: '4px'
    },
    historyCardDate: {
      fontSize: '0.85rem',
      color: '#94a3b8'
    },
    historyCardInfoRow: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0',
      fontSize: '0.9rem'
    },
    historyCardLabel: {
      fontSize: '0.9rem',
      color: '#94a3b8',
      fontWeight: '500'
    },
    historyCardValue: {
      fontSize: '0.9rem',
      color: '#f1f5f9',
      fontWeight: '500'
    },
    historyCardSpacer: {
      color: '#475569',
      margin: '0 8px'
    },
    historyActions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      alignItems: 'flex-end'
    },
    ratingContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px'
    },
    ratingLabel: {
      fontSize: '0.85rem',
      color: 'white',
      fontWeight: '500'
    },
    starRating: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    },
    star: {
      fontSize: '1.2rem',
      transition: 'color 0.2s',
      userSelect: 'none'
    },
    ratingText: {
      fontSize: '0.85rem',
      color: 'white',
      fontWeight: '500',
      marginLeft: '4px'
    },
    historyStatus: {
      background: '#10b981',
      color: '#ffffff',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '0.85rem',
      fontWeight: '500'
    },
    deliverablesButton: {
      padding: '8px 16px',
      background: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '500',
      transition: 'background 0.2s'
    },
    sessionInfo: {
      flex: 1
    },
    sessionInfoTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    sessionInfoText: {
      fontSize: '0.95rem',
      color: '#64748b',
      marginBottom: '4px'
    },
    // Request styles
    requestsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxHeight: '600px',
      overflowY: 'auto',
      padding: '0 4px'
    },
    requestCard: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.3s ease'
    },
    requestHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    trainerInfo: {
      flex: 1
    },
    trainerName: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#1a202c',
      marginBottom: '4px'
    },
    requestStatus: {
      fontSize: '0.9rem',
      color: '#64748b'
    },
    requestTime: {
      fontSize: '0.85rem',
      color: '#94a3b8',
      textAlign: 'right'
    },
    requestDetails: {
      marginBottom: '16px',
      padding: '12px',
      background: 'rgba(248, 250, 252, 0.8)',
      borderRadius: '8px'
    },
    detailRow: {
      fontSize: '0.9rem',
      color: '#374151',
      marginBottom: '6px'
    },
    requestActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end'
    },
    payBtn: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
    },
    joinBtn: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
    },
    joinBtnDisabled: {
      background: '#e2e8f0',
      color: '#94a3b8',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'not-allowed',
      transition: 'all 0.3s ease'
    },
    payBtnDisabled: {
      background: '#64748b',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    waitingForTrainerBtn: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'not-allowed',
      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
      opacity: 0.9
    },
    cancelBtn: {
      background: 'linear-gradient(135deg, #f56565 0%, #e53e3e 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(245, 101, 101, 0.3)'
    },
    // Popup styles
    popupOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    },
    popupContent: {
      background: 'white',
      borderRadius: '16px',
      padding: '0',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      animation: 'popupSlideIn 0.3s ease-out'
    },
    popupHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '20px 24px',
      textAlign: 'center'
    },
    popupTitle: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: '600'
    },
    popupBody: {
      padding: '24px'
    },
    popupMessage: {
      fontSize: '1.1rem',
      color: '#374151',
      marginBottom: '20px',
      textAlign: 'center',
      lineHeight: '1.5'
    },
    popupDetails: {
      background: '#f8fafc',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px'
    },
    popupDetailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    popupLabel: {
      fontSize: '0.9rem',
      color: '#64748b',
      fontWeight: '500'
    },
    popupValue: {
      fontSize: '0.9rem',
      color: '#1f2937',
      fontWeight: '600'
    },
    popupWarning: {
      background: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      padding: '12px',
      marginTop: '16px',
      fontSize: '0.9rem',
      color: '#92400e',
      textAlign: 'center'
    },
    popupActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      padding: '0 24px 24px 24px'
    },
    popupCancelBtn: {
      background: '#f3f4f6',
      color: '#374151',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '100px'
    },
    popupConfirmBtn: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      minWidth: '140px',
      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
    },
    popupFormGroup: {
      marginBottom: '20px'
    },
    popupFormLabel: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#2d3748',
      fontSize: '0.9rem'
    },
    popupFormInput: {
      width: '100%',
      padding: '10px 12px',
      border: '2px solid #e2e8f0',
      borderRadius: '6px',
      fontSize: '0.9rem',
      backgroundColor: 'white'
    },
    popupFormSelect: {
      width: '100%',
      padding: '10px 12px',
      border: '2px solid #e2e8f0',
      borderRadius: '6px',
      fontSize: '0.9rem',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    },
    popupFormTextarea: {
      width: '100%',
      padding: '10px 12px',
      border: '2px solid #e2e8f0',
      borderRadius: '6px',
      fontSize: '0.9rem',
      backgroundColor: 'white',
      resize: 'vertical',
      minHeight: '80px',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    },
    couponHint: {
      fontSize: '0.8rem',
      color: '#64748b',
      marginTop: '4px',
      fontStyle: 'italic'
    },
    paymentSummary: {
      marginTop: '20px',
      padding: '15px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    paymentSummaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    paymentSummaryLabel: {
      fontSize: '0.9rem',
      color: '#475569',
      fontWeight: '500'
    },
    paymentSummaryValue: {
      fontSize: '0.9rem',
      color: '#1e293b',
      fontWeight: '600'
    },
    // New styles for profile editing and session history
    editForm: {
      background: '#f8f9fa',
      borderRadius: '12px',
      padding: '20px',
      border: '2px solid #e2e8f0'
    },
    formActions: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px',
      justifyContent: 'flex-end'
    },
    sessionHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '15px',
      cursor: 'pointer',
      width: '100%'
    },
    expandButton: {
      background: 'transparent',
      border: 'none',
      fontSize: '1.2rem',
      cursor: 'pointer',
      color: '#64748b',
      padding: '5px'
    },
    sessionDetails: {
      marginTop: '15px',
      padding: '15px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    },
    sessionSummary: {
      marginBottom: '20px'
    },
    summaryTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    summaryText: {
      fontSize: '0.9rem',
      color: '#64748b',
      lineHeight: '1.5'
    },
    trainingTasks: {
      marginTop: '15px'
    },
    tasksTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '10px'
    },
    tasksList: {
      maxHeight: '200px',
      overflowY: 'auto'
    },
    taskItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '8px',
      padding: '5px'
    },
    taskCheckbox: {
      width: '16px',
      height: '16px'
    },
    taskText: {
      fontSize: '0.9rem',
      color: '#64748b'
    },
    taskBullet: {
      fontSize: '1.2rem',
      color: '#3b82f6',
      marginRight: '8px',
      fontWeight: 'bold'
    },
    noDeliverables: {
      padding: '20px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px dashed #cbd5e1',
      textAlign: 'center'
    },
    noDeliverablesText: {
      color: '#64748b',
      fontSize: '0.9rem',
      margin: 0,
      fontStyle: 'italic'
    },
    // Session Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: '#1e293b',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '700px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      border: '1px solid #334155'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 25px',
      borderBottom: '1px solid #334155',
      background: '#1e293b'
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#f1f5f9',
      margin: 0
    },
    modalCloseButton: {
      background: 'transparent',
      border: 'none',
      fontSize: '2rem',
      color: '#64748b',
      cursor: 'pointer',
      lineHeight: 1,
      padding: 0,
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      transition: 'all 0.2s ease'
    },
    modalBody: {
      padding: '25px',
      background: '#1e293b'
    },
    modalSessionInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '25px',
      paddingBottom: '25px',
      borderBottom: '1px solid #334155'
    },
    modalInfoRow: {
      display: 'flex',
      alignItems: 'center'
    },
    modalInfoLabel: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#94a3b8'
    },
    modalInfoValue: {
      fontSize: '0.95rem',
      color: '#f1f5f9'
    },
    modalSection: {
      marginBottom: '25px'
    },
    modalSectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#f1f5f9',
      marginBottom: '12px'
    },
    modalSectionText: {
      fontSize: '0.95rem',
      color: '#cbd5e1',
      lineHeight: '1.6',
      maxHeight: '200px',
      overflowY: 'auto',
      padding: '10px',
      background: '#0f172a',
      borderRadius: '8px',
      border: '1px solid #334155'
    },
    modalChecklist: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      textAlign: 'left'
    },
    modalChecklistItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      textAlign: 'left'
    },
    modalChecklistNumber: {
      fontSize: '0.95rem',
      color: '#3b82f6',
      fontWeight: '600',
      lineHeight: '1.6',
      minWidth: '24px'
    },
    modalChecklistText: {
      fontSize: '0.95rem',
      color: '#cbd5e1',
      lineHeight: '1.6',
      flex: 1,
      textAlign: 'left'
    },
    modalNoDeliverables: {
      fontSize: '0.95rem',
      color: '#94a3b8',
      fontStyle: 'italic',
      textAlign: 'center',
      padding: '20px',
      background: '#0f172a',
      borderRadius: '8px',
      border: '1px dashed #334155'
    },
    modalFooter: {
      padding: '20px 25px',
      borderTop: '1px solid #334155',
      display: 'flex',
      justifyContent: 'flex-end',
      background: '#1e293b'
    },
    modalCloseBtn: {
      padding: '10px 24px',
      background: 'linear-gradient(45deg, #e67e22, #f39c12)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    // Training tasks styles
    tasksGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '20px',
      maxHeight: '600px',
      overflowY: 'auto',
      paddingRight: '10px'
    },
    taskCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      border: '2px solid #e2e8f0',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease'
    },
    taskHeader: {
      marginBottom: '15px'
    },
    taskTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '8px'
    },
    taskMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '0.8rem',
      color: '#64748b'
    },
    taskSession: {
      background: '#e2e8f0',
      padding: '2px 8px',
      borderRadius: '12px',
      fontWeight: '500'
    },
    taskDate: {
      color: '#94a3b8'
    },
    taskDetails: {
      marginBottom: '15px'
    },
    taskTrainer: {
      fontSize: '0.9rem',
      color: '#64748b',
      marginBottom: '8px'
    },
    taskNotes: {
      fontSize: '0.85rem',
      color: '#94a3b8',
      fontStyle: 'italic',
      lineHeight: '1.4'
    },
    taskStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px',
      background: '#f8f9fa',
      borderRadius: '8px'
    },
    taskStatusText: {
      fontSize: '0.9rem',
      fontWeight: '500',
      color: '#64748b'
    }
  };

  // If no clientId and not logged in, show error message
  if (!clientId && !isLoggedIn) {
    console.log('No clientId found and not logged in - showing error');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        color: 'white',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        <h1>Access Denied</h1>
        <p>You need to be logged in to access the client portal.</p>
        <button 
          onClick={onBackToHome}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(45deg, #e67e22, #f39c12)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            marginTop: '20px'
          }}
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Top Navigation Bar */}
      <div style={styles.topNav}>
        <div style={styles.navHeader}>
          <div style={styles.logo}>
            Virtual Dog Training
          </div>
          <div style={styles.navActions}>
            <button onClick={onLogout} style={styles.logoutButton}>Logout</button>
          </div>
        </div>
        
        {/* Horizontal Tabs */}
        <div style={styles.horizontalTabList}>
          <button
            onClick={() => handleTabChange('find-trainer')}
            style={{
              ...styles.horizontalTabButton,
              ...(activeTab === 'find-trainer' ? styles.activeHorizontalTabButton : {})
            }}
          >
            Find Trainer
          </button>
          <button
            onClick={() => handleTabChange('requests')}
            style={{
              ...styles.horizontalTabButton,
              ...(activeTab === 'requests' ? styles.activeHorizontalTabButton : {})
            }}
          >
            Training Requests
          </button>
          <button
            onClick={() => handleTabChange('sessions')}
            style={{
              ...styles.horizontalTabButton,
              ...(activeTab === 'sessions' ? styles.activeHorizontalTabButton : {})
            }}
          >
            Sessions
          </button>
          <button
            onClick={() => handleTabChange('profile')}
            style={{
              ...styles.horizontalTabButton,
              ...(activeTab === 'profile' ? styles.activeHorizontalTabButton : {})
            }}
          >
            My Profile
          </button>
          <button
            onClick={() => handleTabChange('pets')}
            style={{
              ...styles.horizontalTabButton,
              ...(activeTab === 'pets' ? styles.activeHorizontalTabButton : {})
            }}
          >
            My Pets
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={styles.mainContent}>
        {/* Tab Content */}
        {activeTab === 'find-trainer' && renderTrainersTab()}
        {activeTab === 'pets' && renderPetsTab()}
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'requests' && renderRequestsTab()}
        {activeTab === 'sessions' && renderSessionsTab()}
      </div>

      {/* Confirmation Popup */}
      {showConfirmPopup && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupContent}>
            <div style={styles.popupHeader}>
              <h3 style={styles.popupTitle}>Confirm Training Request</h3>
            </div>
            <div style={styles.popupBody}>
              <p style={styles.popupMessage}>
                Create an active session request with <strong>{selectedTrainer?.name}</strong>?
              </p>
              <div style={styles.popupDetails}>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Trainer:</span>
                  <span style={styles.popupValue}>{selectedTrainer?.name}</span>
                </div>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Rate:</span>
                  <span style={styles.popupValue}>${selectedTrainer?.baseRate} first 10 min + ${selectedTrainer?.perMinuteRate}/min after</span>
                </div>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Experience:</span>
                  <span style={styles.popupValue}>{selectedTrainer?.experience}</span>
                </div>
              </div>
              
              {/* Pet Selection */}
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Select Pet for Training:</label>
                <select
                  value={selectedPetId}
                  onChange={(e) => setSelectedPetId(e.target.value)}
                  style={styles.popupFormSelect}
                  required
                >
                  <option value="">Choose a pet...</option>
                  {pets.map(pet => (
                    <option key={pet.id} value={pet.id}>
                      {pet.pet_name || pet.name} {pet.race ? `(${pet.race})` : ''} {pet.age ? `- ${pet.age} years old` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Session Duration (minutes):</label>
                <select
                  value={requestDuration}
                  onChange={(e) => setRequestDuration(parseInt(e.target.value, 10))}
                  style={styles.popupFormSelect}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                </select>
              </div>
              
              {/* Explanation */}
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Training Request Explanation:</label>
                <textarea
                  value={requestExplanation}
                  onChange={(e) => setRequestExplanation(e.target.value)}
                  placeholder="Please explain what you'd like to work on with your pet during this training session..."
                  style={styles.popupFormTextarea}
                  rows={4}
                  required
                />
              </div>
            </div>
            <div style={styles.popupActions}>
              <button
                className="popup-cancel-btn"
                style={styles.popupCancelBtn}
                onClick={handleCancelTraining}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="popup-confirm-btn"
                style={styles.popupConfirmBtn}
                onClick={handleConfirmTraining}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Yes, Create Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Pet Modal */}
      {showAddPetModal && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupContent}>
            <div style={styles.popupHeader}>
              <h3 style={styles.popupTitle}>Add New Pet</h3>
            </div>
            <form onSubmit={handleAddPet} style={styles.popupForm}>
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Pet Name *</label>
                <input
                  type="text"
                  value={newPetData.pet_name}
                  onChange={(e) => setNewPetData({ ...newPetData, pet_name: e.target.value })}
                  style={styles.popupFormInput}
                  required
                />
              </div>
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Age (years) *</label>
                <input
                  type="number"
                  value={newPetData.age}
                  onChange={(e) => setNewPetData({ ...newPetData, age: e.target.value })}
                  style={styles.popupFormInput}
                  required
                  min="0"
                />
              </div>
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Breed *</label>
                <input
                  type="text"
                  value={newPetData.race}
                  onChange={(e) => setNewPetData({ ...newPetData, race: e.target.value })}
                  style={styles.popupFormInput}
                  required
                />
              </div>
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Target Behaviors</label>
                <textarea
                  value={newPetData.target_behaviors}
                  onChange={(e) => setNewPetData({ ...newPetData, target_behaviors: e.target.value })}
                  style={styles.popupFormTextarea}
                  rows={3}
                  placeholder="e.g., leash pulling, jumping on people, separation anxiety..."
                />
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <div style={styles.popupActions}>
                <button
                  type="button"
                  className="popup-cancel-btn"
                  style={styles.popupCancelBtn}
                  onClick={() => {
                    setShowAddPetModal(false);
                    setNewPetData({
                      pet_name: '',
                      age: '',
                      race: '',
                      target_behaviors: ''
                    });
                    setError(null);
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="popup-confirm-btn"
                  style={styles.popupConfirmBtn}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Pet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Popup */}
      {showCancelPopup && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupContent}>
            <div style={styles.popupHeader}>
              <h3 style={styles.popupTitle}>Cancel Training Request</h3>
            </div>
            <div style={styles.popupBody}>
              <p style={styles.popupMessage}>
                Are you sure you want to cancel your training request with <strong>{selectedRequest?.trainer_name}</strong>?
              </p>
              <div style={styles.popupDetails}>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Trainer:</span>
                  <span style={styles.popupValue}>{selectedRequest?.trainer_name}</span>
                </div>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Status:</span>
                  <span style={styles.popupValue}>{selectedRequest?.status}</span>
                </div>
                <div style={styles.popupDetailRow}>
                  <span style={styles.popupLabel}>Requested:</span>
                  <span style={styles.popupValue}>{selectedRequest?.created_at ? new Date(selectedRequest.created_at).toLocaleString() : 'N/A'}</span>
                </div>
              </div>
              <div style={styles.popupWarning}>
                This action cannot be undone. You will need to create a new request if you want to train with this trainer again.
              </div>
            </div>
            <div style={styles.popupActions}>
              <button
                className="popup-cancel-btn"
                style={styles.popupCancelBtn}
                onClick={handleCancelCancel}
                disabled={loading}
              >
                No, Keep Request
              </button>
              <button
                className="popup-confirm-btn"
                style={styles.popupConfirmBtn}
                onClick={handleConfirmCancel}
                disabled={loading}
              >
                {loading ? 'Cancelling...' : 'Yes, Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Code Modal */}
      {showCouponModal && selectedRequestForPayment && (
        <div style={styles.popupOverlay} onClick={() => { setShowCouponModal(false); setCouponCode(''); setSelectedRequestForPayment(null); }}>
          <div style={styles.popupContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.popupHeader}>
              <h3 style={styles.popupTitle}>Enter Payment Details</h3>
            </div>
            <div style={styles.popupBody}>
              <div style={styles.popupFormGroup}>
                <label style={styles.popupFormLabel}>Coupon Code (Optional):</label>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  style={styles.popupFormInput}
                  maxLength={50}
                />
                <p style={styles.couponHint}>You can also enter a promo code during checkout on Stripe</p>
              </div>
              <div style={styles.paymentSummary}>
                <div style={styles.paymentSummaryRow}>
                  <span style={styles.paymentSummaryLabel}>Session Cost:</span>
                  <span style={styles.paymentSummaryValue}>{formatCurrency(selectedRequestForPayment.total_price || 0)}</span>
                </div>
                {couponCode && (
                  <div style={styles.paymentSummaryRow}>
                    <span style={styles.paymentSummaryLabel}>Coupon Applied:</span>
                    <span style={styles.paymentSummaryValue}>{couponCode}</span>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.popupActions}>
              <button
                className="popup-cancel-btn"
                style={styles.popupCancelBtn}
                onClick={() => { setShowCouponModal(false); setCouponCode(''); setSelectedRequestForPayment(null); }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="popup-confirm-btn"
                style={styles.popupConfirmBtn}
                onClick={handleApplyCouponAndPay}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortal;